import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/trpc/trpc";
import { TagDialog, type TagForEdit } from "@/components/tag-dialog";

export const Route = createFileRoute("/_authenticated/tags")({
  component: TagsPage,
});

function TagsPage() {
  const { data: tags, isLoading } = trpc.tag.list.useQuery();
  const utils = trpc.useUtils();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TagForEdit | null>(null);

  const toggleActive = trpc.tag.update.useMutation({
    onSuccess: () => utils.tag.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  const deleteTag = trpc.tag.delete.useMutation({
    onSuccess: () => {
      utils.tag.list.invalidate();
      toast.success("Tag usunięty");
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tagi</h1>
          <p className="text-sm text-muted-foreground">
            {tags?.length ?? 0} tagów · Kategorie używane w wyszukiwarce na ekranie głównym aplikacji
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="mr-1 size-4" />
          Dodaj tag
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Nazwa</th>
                <th className="px-4 py-3 font-medium">Zabiegi</th>
                <th className="px-4 py-3 font-medium">Kolejność</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {tags?.map((t) => (
                <tr key={t.id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-medium">{t.name}</td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary" className="text-xs">
                      {t.treatmentIds.length} zabiegów
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {t.sortOrder}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant={t.isActive ? "default" : "secondary"}
                      className="cursor-pointer"
                      onClick={() =>
                        toggleActive.mutate({ id: t.id, isActive: !t.isActive })
                      }
                    >
                      {t.isActive ? "Aktywny" : "Nieaktywny"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditing({
                            id: t.id,
                            name: t.name,
                            sortOrder: t.sortOrder,
                            isActive: t.isActive,
                            treatmentIds: t.treatmentIds,
                          });
                          setDialogOpen(true);
                        }}
                      >
                        Edytuj
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm(`Usunąć tag "${t.name}"?`)) {
                            deleteTag.mutate({ id: t.id });
                          }
                        }}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {(tags?.length ?? 0) === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    Brak tagów. Dodaj kategorie, aby ułatwić klientom wyszukiwanie zabiegów.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <TagDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        tag={editing}
      />
    </div>
  );
}
